if (!customElements.get("ymm-form")) {
  class YearMakeModelForm extends HTMLElement {
    VEHICLES_CACHE_KEY = "yme_vehicles_cache";
    formId = this.dataset.formId;
    urlParams = new URLSearchParams(window.location.search);

    constructor() {
      super();

      this.submit = this.submit.bind(this);
      this.reset = this.reset.bind(this);
    }

    connectedCallback() {
      document.addEventListener("DOMContentLoaded", () => {
        this.selectElements();
        this.attachListeners();
        this.init();
        this.syncPhotoPointsNotice();
      });
    }

    async init() {
      // YME extension should always save filter options to storage
      const vehicles = this.loadVehicles();
      if (vehicles?.length) this.vehicles = vehicles;
      else this.vehicles = await this.getVehicles();
      this.populateMakeOptions(this.vehicles.map((entry) => entry.make).sort());
    }

    selectElements() {
      const selectors = this.dataset.selectors?.split(",") ?? [];
      this.makeSelect = this.querySelector(`select[name=${selectors[0]}]`);
      this.yearSelect = this.querySelector(`select[name=${selectors[1]}]`);
      this.engineSelect = this.querySelector(`select[name=${selectors[2]}]`);

      this.form = this.querySelector(`form#${this.formId}`);
      this.teifiUpload = this.querySelector("teifi-upload");
      this.submitButton = this.querySelector("button#save-truck");
      this.photoPointsNotice = this.querySelector(".jsGaragePhotoPointsAddNotice");
      this.photoPointsMessage = this.querySelector(".jsGaragePhotoPointsAddMessage");
    }

    loadVehicles() {
      try {
        const raw = localStorage.getItem(VEHICLES_CACHE_KEY);
        if (!raw) return null;
        const json = JSON.parse(raw);
        return json.data;
      } catch (err) {
        console.warn("Failed to load vehicles from cache", err);
      }
    }

    async getVehicles() {
      const results = [];
      let hasNextPage = true;
      let after = null;
      while (hasNextPage) {
        try {
          const res = await window.StorefrontApi.request({
            query: `
              query getAllValidVehicles($after: String) {
                metaobjects(type: "fitment_vehicle", first: 250, after: $after) {
                  edges {
                    node {
                      id
                      handle
                      year: field(key: "year") {
                        value
                      }
                      make: field(key: "make") {
                        value
                      }
                      model: field(key: "model") {
                        value
                      }
                      engine: field(key: "engine") {
                        value
                      }
                    }
                  }
                  pageInfo {
                    hasNextPage
                    endCursor
                  }
                }
              }
          `,
            variables: {
              after,
            },
          });
          hasNextPage = res.metaobjects.pageInfo.hasNextPage;
          after = res.metaobjects.pageInfo.endCursor;
          results.push(
            ...res.metaobjects.edges.map((edge) => ({
              ...edge.node,
              year: edge.node.year?.value,
              make: edge.node.make?.value,
              model: edge.node.model?.value,
              engine: edge.node.engine?.value,
            })),
          );
        } catch (err) {
          console.error(err);
          throw err;
        }
      }

      return results;
    }

    populateYearOptions(years = []) {
      if (!this.yearSelect || !years?.length) return;

      const _years = [...new Set(years)];
      this.yearSelect.innerHTML = `
        <option value="">Year</option>
        ${_years.map((year) => `<option value="${year}">${year}</option>`).join("")}
      `;
      this.yearSelect.disabled = false;
    }

    populateMakeOptions(makes = []) {
      if (!this.makeSelect || !makes?.length) return;

      const _makes = [...new Set(makes)];
      this.makeSelect.innerHTML = `
        <option value="">Make</option>
        ${_makes.map((make) => `<option value="${make}">${make}</option>`).join("")}
      `;
      this.makeSelect.disabled = false;
    }

    populateEngineOptions(engines = []) {
      if (!this.engineSelect || !engines?.length) return;

      const _engines = [...new Set(engines)];
      this.engineSelect.innerHTML = `
        <option value="">Engine</option>
        ${_engines.map((engine) => `<option value="${engine}">${engine}</option>`).join("")}
      `;
      this.engineSelect.disabled = false;
    }

    async submit(e) {
      e?.preventDefault();
      const params = new URLSearchParams();
      params.set("make", this.currentMake);
      params.set("year", this.currentYear);
      params.set("engine", this.currentEngine);
      const query = params.toString();
      const data = { query };

      this.submitButton?.classList.add("loading");
      //TODO: disable shop button until upload is complete
      const callback = () => {
        this.submitButton?.classList.remove("loading");
        this.teifiUpload?.resetForm();
        this.reset();
      };

      const upload = this.querySelector("teifi-upload");
      if (upload) {
        const image = await upload.submit();
        if (!image) {
          callback();
          return;
        }
        data.image = image;
      }

      sessionStorage.setItem("garageSelected", query);
      window.theme.debounceEventDispatcher.dispatch("garage:action", {
        type: "add",
        data,
        callback,
      });
    }

    onFormChange() {
      if (!this.currentMake || !this.currentYear || !this.currentEngine) {
        this.submitButton.disabled = true;
      }

      if (!this.currentMake) this.reset();
      else if (!this.currentYear) {
        this.engineSelect.innerHTML = `<option value="">Engine</option>`;
        this.engineSelect.disabled = true;
      }
    }

    getGaragePhotoRewardState() {
      const garage = document.querySelector("teifi-garage");
      const garageState = garage?.getPhotoRewardPromptState?.();
      if (garageState) return garageState;
      return this.dataset.photoRewardEligible === "true"
        ? { state: "default", vehicleName: "" }
        : { state: "hidden", vehicleName: "" };
    }

    syncPhotoPointsNotice(state = this.getGaragePhotoRewardState()) {
      if (!this.photoPointsNotice || !this.photoPointsMessage) return;
      if (!state || state.state === "hidden") {
        this.photoPointsNotice.hidden = true;
        return;
      }
      const defaultMessage = this.photoPointsNotice.dataset.defaultMessage;
      const vehicleMessage = this.photoPointsNotice.dataset.vehicleMessage;
      const successMessage = this.photoPointsNotice.dataset.successMessage;
      const message =
        state.state === "success"
          ? successMessage
          : state.state === "vehicle" && state.vehicleName
            ? vehicleMessage.replace("[vehicle]", state.vehicleName)
            : defaultMessage;

      this.photoPointsMessage.textContent = message;
      this.photoPointsNotice.classList.toggle("is-success", state.state === "success");
      this.photoPointsNotice.hidden = false;
    }

    reset() {
      this.makeSelect.value = "";
      this.yearSelect.innerHTML = `<option value="">Year</option>`;
      this.engineSelect.innerHTML = `<option value="">Engine</option>`;
      this.yearSelect.disabled = true;
      this.engineSelect.disabled = true;
      this.submitButton.disabled = true;
    }

    attachListeners() {
      window.addEventListener("garage:photo-reward", (event) => {
        this.syncPhotoPointsNotice(event.detail);
      });
      this.makeSelect?.addEventListener("change", (e) => {
        const make = e.target.value;
        this.populateYearOptions(
          this.vehicles
            .filter((entry) => entry.make === make)
            .map((entry) => entry.year)
            .sort((a, b) => b - a),
        );
      });
      this.yearSelect?.addEventListener("change", (e) => {
        const year = e.target.value;
        this.populateEngineOptions(
          this.vehicles
            .filter(
              (entry) => entry.make === this.currentMake && entry.year === year,
            )
            .map((entry) => entry.engine),
        );
      });
      this.engineSelect?.addEventListener("change", (e) => {
        if (e.target.value) this.submitButton.disabled = false;
      });

      [this.makeSelect, this.yearSelect, this.engineSelect].forEach((select) =>
        select?.addEventListener("change", this.onFormChange.bind(this)),
      );

      this.submitButton?.addEventListener("click", this.submit);
    }

    get currentYear() {
      return this.yearSelect?.value;
    }

    get currentMake() {
      return this.makeSelect?.value;
    }

    get currentEngine() {
      return this.engineSelect?.value;
    }
  }
  customElements.define("ymm-form", YearMakeModelForm);
}
